import {
  Component,
  OnChanges,
  OnInit,
  SimpleChanges,
  computed,
  effect,
  input,
  signal,
  ChangeDetectionStrategy,
  inject,
  OnDestroy,
  output,
} from '@angular/core';
import { PageEvent } from '@angular/material/paginator';

import { translate } from '@jsverse/transloco';

import { AppSharedModule } from 'app/app-shared.module';
import { FilterService } from 'app/modules/collection/services/filter.service';
import { CustomFieldsService } from 'app/modules/field/custom-fields.service';
import { PptxReport } from 'app/modules/widget/services/pptx.service';
import { AuthService } from 'app/providers/bridge-api/auth.service';
import { WidgetDefault } from 'app/providers/bridge-api/dashboard.service';
import { AiInsight, AiInsightsService } from 'app/providers/semantria-api/ai-insights.service';
import {
  AiInsightFeed,
  AiInsightsGroupFeedService,
} from 'app/providers/storage-api/ai-insights-group-feeds.service';
import { StorageAccountService } from 'app/providers/storage-api/blender-account.service';
import { InteractiveSmartSummaryService } from 'app/providers/storage-api/interactive-smart-summary.service';
import { AlertService } from 'app/services/alert.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { SystemService } from 'app/services/system.service';
import { TimezoneService } from 'app/services/timezone.service';

import { RealtimeConnection, RealtimeMessage } from '../../../../providers/realtime.service';
import { GroupService } from '../../../../providers/semantria-api/group.service';
import { ItemLabel } from '../../../../providers/storage-api/object-label.service';
import { AbstractWidgetComponent } from '../widget/abstract-widget.component';
import { WidgetComponent } from '../widget/widget.component';
import {
  InsightCardComponent,
  InsightCardOptions,
} from '../widget-expansion-panel/widget-ai-insights/insight-card/insight-card.component';
import { InsightImageLoaderService } from '../../services/insight-image-loader.service';
import { WidgetCommentsHandlerService } from '../../services/widget-comments-handler.service';

export type WidgetInsightFeedSource = 'allProjectFeeds' | 'dashboardFeeds';
export interface WidgetInsightFeedOptions extends WidgetDefault {
  feedSource?: WidgetInsightFeedSource;
}

@EnableClassTelemetry()
@Component({
  selector: 'app-widget-insight-feed',
  imports: [AppSharedModule, InsightCardComponent],
  standalone: true,
  templateUrl: './widget-insight-feed.component.html',
  styleUrls: ['./widget-insight-feed.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WidgetInsightFeedComponent
  extends AbstractWidgetComponent<WidgetInsightFeedOptions>
  implements OnInit, OnChanges, OnDestroy
{
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  selectedInsightFeed = input<AiInsightFeed>(undefined);
  sortInsightToggle = input<boolean>(false);

  feedEvent = output<Pick<RealtimeMessage<AiInsightFeed>, 'verb' | 'object'>>();

  private readonly insightImageLoader = inject(InsightImageLoaderService);

  selectedInsights: AiInsight[] = [];
  insightFeedSelectAll = false;
  readonly pageSizeOptions = [5, 15, 50];

  private readonly insightResponse = signal<AiInsight[]>([]);
  private readonly pageIndex = signal(0);
  private readonly pageSize = signal(this.pageSizeOptions[0]);

  readonly insightCardOptions = computed<InsightCardOptions>(() => ({
    canGenerateInsights: this.authService.canGenerateRevealInsights(),
    isReadOnly: this.widgetComponent.isReadOnly,
    currentUserId: this.authService.currentUserId,
  }));

  readonly pagedInsights = computed<AiInsight[]>(() => {
    const insights = this.insightResponse();
    if (!insights.length) {
      return [];
    }

    const start = this.pageIndex() * this.pageSize();
    return this.sortInsights(insights).slice(start, start + this.pageSize());
  });

  private feedRtrConnection: RealtimeConnection;

  constructor(
    protected systemService: SystemService,
    protected accountService: StorageAccountService,
    protected authService: AuthService,
    protected timezoneService: TimezoneService,
    protected filterService: FilterService,
    protected alertService: AlertService,
    protected interactiveSmartSummaryService: InteractiveSmartSummaryService,
    protected widgetComponent: WidgetComponent,
    protected customFieldService: CustomFieldsService,
    protected commentsHandlerService: WidgetCommentsHandlerService,
    private aiInsightsService: AiInsightsService,
    private aiInsightsGroupFeedService: AiInsightsGroupFeedService,
    private groupService: GroupService
  ) {
    super(
      systemService,
      accountService,
      authService,
      timezoneService,
      filterService,
      alertService,
      interactiveSmartSummaryService,
      widgetComponent,
      customFieldService,
      commentsHandlerService
    );

    this.needDataSource = false;

    effect(() => {
      const selectedFeedId = this.selectedInsightFeed();
      if (!selectedFeedId) {
        this.updateWidgetState([]);

        return;
      }

      this.loadSelectedInsightFeedDetails();
    });

    effect(() => {
      const insightsLength = this.insightResponse().length;
      const currentIndex = this.pageIndex();

      if (!insightsLength) {
        if (currentIndex !== 0) {
          this.pageIndex.set(0);
        }
        return;
      }

      const totalPages = Math.max(Math.ceil(insightsLength / this.pageSize()), 1);
      const nextIndex = Math.min(currentIndex, totalPages - 1);
      if (nextIndex !== currentIndex) {
        this.pageIndex.set(nextIndex);
      }
    });
  }

  private getInsightTimestamp(insight: AiInsight): number {
    return new Date(insight.creation_date).getTime() || 0;
  }

  private sortInsights(insights: AiInsight[] = []): AiInsight[] {
    return [...insights].sort((insightA, insightB) => {
      const creationTimeDiff =
        this.getInsightTimestamp(insightA) - this.getInsightTimestamp(insightB);
      return this.sortInsightToggle() ? creationTimeDiff : -creationTimeDiff;
    });
  }

  ngOnInit() {
    this.loadingStarted.emit(true);

    this.feedRtrConnection = this.aiInsightsGroupFeedService.on(
      this.groupService.getSelectedGroupId(),
      this.handleFeedEvent.bind(this)
    );
  }

  ngAfterViewInit() {
    this.loadingFinished.emit(true);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.widget) {
      this.loadSelectedInsightFeedDetails();
    }
  }

  ngOnDestroy(): void {
    this.feedRtrConnection.disconnect();
  }

  private handleFeedEvent(data: RealtimeMessage<AiInsightFeed>) {
    this.feedEvent.emit({
      verb: data.verb,
      object: data.object,
    });
  }

  private loadSelectedInsightFeedDetails(): void {
    if (!this.selectedInsightFeed()) {
      return;
    }

    this.isLoading = true;

    this.aiInsightsGroupFeedService
      .getAiInsightsFeedsDetailsForSelectedFeed(this.selectedInsightFeed().id)
      .then((result) => {
        const insights = result ?? [];
        this.updateWidgetState(insights);
      })
      .finally(() => {
        this.isLoading = false;
        this.cdr.markForCheck();
      });
  }

  private updateWidgetState(insights: AiInsight[]): void {
    this.insightResponse.set(insights);
    this.selectedInsights = [];
    this.resetPagination();
    this.syncSelectAllState();
  }

  public onInsightSelectionChange(event: { insight: AiInsight; selected: boolean }): void {
    const alreadySelected = this.selectedInsights.some(
      (s) => s.insight_id === event.insight.insight_id
    );
    if (event.selected && !alreadySelected) {
      this.selectedInsights = [...this.selectedInsights, event.insight];
    }
    if (!event.selected && alreadySelected) {
      this.selectedInsights = this.selectedInsights.filter(
        (s) => s.insight_id !== event.insight.insight_id
      );
    }
    this.syncSelectAllState();
  }

  public onSelectAllClicked(event?: Event): void {
    event.preventDefault();

    if (this.insightFeedSelectAll) {
      this.selectedInsights = [];
    } else {
      const mapped = new Map(this.selectedInsights.map((s) => [s.insight_id, s] as const));
      this.insightResponse().forEach((insight) => mapped.set(insight.insight_id, insight));
      this.selectedInsights = Array.from(mapped.values());
    }

    this.syncSelectAllState();
  }

  async onPublishInsight($event: { insight: AiInsight; labels: Partial<ItemLabel>[] }) {
    if (!$event?.insight) {
      return;
    }

    const insight = $event.insight;
    const labels = $event.labels ?? [];
    const tags = labels.map((l) => l.label?.trim()).filter(Boolean);

    const params: Partial<AiInsight> = {
      properties: {
        ...insight.properties,
        shortPurpose: insight.properties.shortPurpose,
        includeImage: insight.properties.includeImage,
      },
      comment: insight.comment,
      published: true,
      tags,
    };

    try {
      const updateInsight = this.aiInsightsService.updateInsight(insight.insight_id, params);
      const updateLocalization = this.aiInsightsService
        .deleteInsightLocalization(insight.insight_id, insight.localizations[0].language)
        .then(() =>
          this.aiInsightsService.addInsightLocalization(insight.insight_id, {
            text: insight.localizations[0].text,
            language: insight.localizations[0].language,
          })
        );

      const [updatedInsight, updatedLocalization] = await Promise.all([
        updateInsight,
        updateLocalization,
      ]);

      const updated: AiInsight = {
        ...updatedInsight,
        localizations: [updatedLocalization],
      };

      // Keep feed UI + selection in sync with updated insight values.
      this.insightResponse.update((response) =>
        response.map((i) => (i.insight_id === updated.insight_id ? updated : i))
      );
      this.selectedInsights = this.selectedInsights.map((i) =>
        i.insight_id === updated.insight_id ? updated : i
      );
    } catch (e) {
      this.alertService.showErrorToast(
        translate('widgets.ai-insights.could-not-publish-insight'),
        e
      );
    }
  }

  public onPageChanged(event: PageEvent): void {
    this.pageIndex.set(event.pageIndex);
    this.pageSize.set(event.pageSize);
  }

  public isInsightSelected(insight: AiInsight): boolean {
    return this.selectedInsights.some((item) => item.insight_id === insight.insight_id);
  }

  private syncSelectAllState(): void {
    this.insightFeedSelectAll =
      !!this.insightResponse()?.length &&
      this.selectedInsights.length === this.insightResponse().length;
    this.widgetComponent.onInsightFeedSelectionChange(this.selectedInsights);
  }

  private resetPagination(): void {
    this.pageIndex.set(0);
  }

  override async exportSelectedInsightsAsPptx(
    pptx: PptxReport,
    insights: AiInsight[]
  ): Promise<void> {
    if (!insights?.length) {
      return;
    }

    for (const insight of insights) {
      const title = insight.properties.shortPurpose;
      const summary = insight.properties.summary || '';
      const body = insight.localizations?.[0].text || summary;
      const createdAt = insight.creation_date
        ? new Date(insight.creation_date).toLocaleString()
        : '';
      const tags = this.extractInsightTags(insight);

      const slideText = `${summary ? summary + '\n\n' : ''}${body}`.trim();

      let image: { path: string } | undefined;
      try {
        const loaded = await this.insightImageLoader.load(
          insight.insight_id,
          insight.images?.[0]?.image_id
        );
        if (typeof loaded === 'string') {
          image = { path: loaded };
        }
      } catch {
        // Ignore image failures during export and fall back to text-only slide.
      }

      pptx.addInsightTextSlide({
        title: `${title}${createdAt ? ` - ${createdAt}` : ''}`,
        body: slideText || title,
        tags,
        comments: insight.comment,
        image,
      });
    }
  }

  private extractInsightTags(insight: AiInsight): string[] {
    const rawTags = insight.tags ?? (insight.properties as any)?.tags;
    if (!rawTags) {
      return [];
    }

    const toArray = Array.isArray(rawTags) ? rawTags : [rawTags];
    return toArray.flatMap((tag) => {
      if (!tag || typeof tag !== 'string') {
        return [];
      }

      const trimmed = tag.trim();
      try {
        const parsed = JSON.parse(trimmed);
        const label = (parsed?.label).toString().trim() || trimmed;
        return label ? [label] : [];
      } catch {
        return trimmed ? [trimmed] : [];
      }
    });
  }

  public isWidgetGroupChangeDisabled(): boolean {
    return false;
  }

  public canChangeAnalyticItem(): boolean {
    return false;
  }

  public canApplyComparison(): boolean {
    return false;
  }

  public canAddToWidgetGroup() {
    return false;
  }

  public canAddToComparison(): boolean {
    return false;
  }

  public insightFeedsWidgetActions(): boolean {
    return true;
  }

  public canCreateCollection(): boolean {
    return false;
  }

  widgetMenuDisplayOnHover(): boolean {
    return false;
  }

  canHideTitle(): boolean {
    return false;
  }

  public actionsDisabled() {
    return false;
  }

  public canShowDataTable(): boolean {
    return false;
  }

  get widgetQueryOptions() {
    return null;
  }

  get canDownload(): boolean {
    return false;
  }

  get defaultTitle() {
    return translate('widgets.insight-feed');
  }
}
